import React from 'react';

import _ from 'lodash';
import { connect } from 'react-redux';

import { InputMoney } from '@nerdwallet/currency';
import num from '@nerdwallet/nw-client-lib/num';

import { inputOnChange, INPUT_TRACKING_TYPES } from '../../../lib/utils';
import type { MonthlyPaymentInputProps } from '../types';
import {
  getFieldPlaceholderByCalculator,
  getFieldValueByCalculator,
} from '../utils';
import { debouncedTrackInputEvent } from '../../../lib/analytics/trackers';
import useAnalytics from '../../../lib/analytics/use-analytics';

type OnChangeEvent = React.ChangeEvent<HTMLInputElement>;

const HomeownersAssociationFees = (
  props: MonthlyPaymentInputProps
): JSX.Element => {
  const segmentAbstract = useAnalytics();
  const {
    placeholder,
    value,
    error,
    errorMessage,
    calculator,
    dispatch,
    analytics,
    label = 'Homeowners association (HOA) fees',
    tooltip = 'Dues that are used by a homeowners association — a group that manages planned neighborhoods or condo communities. Payments go toward maintenance of common areas used by all homeowners.',
    fullWidth = true,
  } = props;

  const analyticsEnabled = analytics?.enabled;
  const legacyAnalytics =
    analyticsEnabled && analytics?.payload?.legacy?.formName;
  const bedrockAnalytics =
    analyticsEnabled && analytics?.payload?.bedrock?.eventType;

  const onInputChange = (e: OnChangeEvent) => {
    let legacy;
    if (legacyAnalytics) {
      const legacyProps = analytics?.payload?.legacy;
      legacy = {
        ...legacyProps,
        fieldName: 'HOADues',
        inputType: INPUT_TRACKING_TYPES.TEXT,
      };
    }

    let bedrock;
    if (bedrockAnalytics) {
      const bedrockProps = analytics?.payload?.bedrock;
      bedrock = {
        ...bedrockProps,
        valueFieldName: 'value_i',
      };
    }

    // segment analytics
    debouncedTrackInputEvent(
      {
        entityName: 'homeowners_association_(hoa)_fees_monthly',
        field: 'Homeowners Association (HOA) Fees',
        field_type: 'user_type_input',
        field_value: e.target.value,
      },
      segmentAbstract
    );

    inputOnChange({
      event: e,
      dispatch,
      overridesValue: 'monthlyPayments',
      overrides: true,
      legacy,
      bedrock,
      fieldName: 'hoa',
      calculator,
      inputType: 'currency',
    });
  };

  const placeholderValue = _.isNil(placeholder)
    ? ''
    : num.formatCurrency(placeholder);
  const currencyValue = _.isNil(value) ? '' : num.formatCurrency(value);

  return (
    <InputMoney
      label={label}
      tooltip={tooltip}
      placeholder={placeholderValue}
      value={currencyValue}
      error={error}
      errorMessage={errorMessage}
      onChange={onInputChange}
      fullWidth={fullWidth}
    />
  );
};

const mapStateToProps = (state, ownProps: MonthlyPaymentInputProps) => {
  const { calculator } = ownProps;
  const errorMessage = state?.validations?.hoa?.[0];
  const error = !!errorMessage;

  return {
    error,
    errorMessage,
    value: getFieldValueByCalculator(
      state,
      calculator,
      'homeownersAssociationFees'
    ),
    placeholder: String(
      getFieldPlaceholderByCalculator(
        state,
        calculator,
        'homeownersAssociationFees'
      )
    ),
  };
};

export default connect(mapStateToProps)(HomeownersAssociationFees);
